import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PesticideInputForm from '../components/PesticideInputForm.tsx';
import { scheduleAPI } from '../services/api.ts';

// Define the API request interface
interface PesticidesRequest {
  Id: number;
  PesticideId: number;
  IngredientId: number;
  ClassId: number;
  QuantityOfFinal: string;
  ApplicationId: number;
  IsDirty: boolean;
}

const PesticideApplicationRecord: React.FC = () => {
  const navigate = useNavigate();
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Log scheduleId on component mount for internal tracking only
  useEffect(() => {
    console.log("PesticideApplicationRecord MOUNTED with scheduleId:", scheduleId);
    console.log("scheduleId type:", typeof scheduleId);
  }, [scheduleId]);

  const handleSave = async (entries: PesticidesRequest[]) => {
    try {
      setSaving(true);
      setSaveError(null);

      console.log("About to call API with scheduleId:", scheduleId);
      
      // Call the API directly with the raw scheduleId from URL
      const response = await scheduleAPI.updatePesticides(scheduleId as string, entries);
      console.log("API response:", response);
      
      setSaveSuccess(true);
      
      // Show success message temporarily
      setTimeout(() => {
        setSaveSuccess(false);
        
        // Reload the page to get fresh data after save
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error("Error updating pesticide data:", error);
      setSaveError("Failed to update pesticide data. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // If the Save Record button is clicked directly
  const handleDirectSave = () => {
    console.log("Direct save triggered, scheduleId:", scheduleId);
    const form = document.querySelector('form');
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      console.error("Could not find the form element");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="mr-4 text-gray-500 hover:text-gray-700"
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Pesticide Application Record</h1>
        </div>
        
        <div className="hidden sm:block">
          <button 
            onClick={() => navigate(-1)}
            className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
          >
            <span>Cancel</span>
          </button>
        </div>
      </div>
      
      {/* Show success message */}
      {saveSuccess && (
        <div className="mb-4 p-3 rounded bg-green-100 text-green-800 flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Pesticide data updated successfully!
        </div>
      )}

      {/* Show error message */}
      {saveError && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-800 flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {saveError}
        </div>
      )}
      
      {/* Pass the scheduleId to the form component */}
      <PesticideInputForm 
        scheduleId={scheduleId as string} 
        onSave={handleSave}
      />

      {/* Bottom save button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end md:static md:bg-transparent md:border-0 md:p-0 md:mt-4">
        <button
          type="button"
          onClick={handleDirectSave}
          disabled={saving}
          className={`bg-green-600 text-white px-6 py-2 rounded-lg flex items-center ${
            saving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-green-700'
          } transition-colors`}
        >
          {saving ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating...
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Save Pesticide Record
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PesticideApplicationRecord; 